(function() {
  'use strict';

  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
    console.error('GSAP or ScrollTrigger is not loaded');
    return;
  }

  // Register ScrollTrigger plugin
  gsap.registerPlugin(ScrollTrigger);

  // Keep browser scrollbar progress aligned with page scroll.
  // Lenis + scrollerProxy can make visual progress diverge from native scrollbar.
  const ENABLE_LENIS_PROXY = false;

  // 体感速度/顺滑度调参（越大越慢、越“拖拽感”）
  const FEEL = {
    // pin 行程（用 ScrollTrigger end 控制，不再靠拉高 section 产生大段空白）
    pinVh: 7, // 略微拉长整体行程
    // ScrollTrigger 跟手程度：稍微“粘”一点，但不至于回滚很拖
    scrub: 2.2, // 降低 scrub，减轻“回第一屏很慢”的感觉
    // Lenis 本体的缓动：回到更轻量的 1.2，减少反向滚动的“惯性拖尾”
    lenisDuration: 1.2,
    // Lenis 的滚轮倍率：适中，让每次滚轮推进刚好一点点
    wheelMultiplier: 0.7
  };

  function setupLenisForScrollTrigger() {
    if (!ENABLE_LENIS_PROXY) return;
    // 单例：避免多个 section 重复初始化 / 重复 scrollerProxy
    if (window.__mtLenisScrollProxySetup) return;

    if (typeof Lenis === 'undefined') {
      // Lenis 没加载时直接跳过，仍可用原生 scroll + scrub
      return;
    }

    window.__mtLenisScrollProxySetup = true;

    // 复用已有实例（如果别的 section 已经创建过）
    if (!window.lenisInstance) {
      window.lenisInstance = new Lenis({
        duration: FEEL.lenisDuration,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        orientation: 'vertical',
        gestureOrientation: 'vertical',
        smoothWheel: true,
        wheelMultiplier: FEEL.wheelMultiplier,
        smoothTouch: false,
        touchMultiplier: 2,
        infinite: false
      });
    }

    const lenis = window.lenisInstance;

    // 让 ScrollTrigger 跟随 Lenis 的滚动值更新
    lenis.on('scroll', () => ScrollTrigger.update());

    // 关键：把 ScrollTrigger 的 scrollTop 读写代理到 Lenis
    ScrollTrigger.scrollerProxy(document.body, {
      scrollTop(value) {
        if (arguments.length) {
          lenis.scrollTo(value, { immediate: true });
        } else {
          return lenis.scroll;
        }
      },
      getBoundingClientRect() {
        return {
          top: 0,
          left: 0,
          width: window.innerWidth,
          height: window.innerHeight
        };
      }
    });

    // RAF：驱动 Lenis
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);

    document.documentElement.classList.add('lenis');
  }

  window.MTScroll = {
    init: function(options) {
      const sectionId = options.sectionId;
      const blockCount = options.blockCount || 0;
      
      if (blockCount < 4) {
        console.warn('MTScroll: At least 4 blocks are required');
        return;
      }

      const section = document.getElementById(sectionId);
      if (!section) {
        console.error('MTScroll: Section not found');
        return;
      }
      const main = document.querySelector('main');
      let autoSwitchHost = section;
      let autoSwitchHostIndex = -1;
      const DEBUG = true;
      let lastReadyState = '';
      let lastPinnedState = '';
      const debugLog = (...args) => {
        if (!DEBUG) return;
        console.log('[gw-scroll debug]', ...args);
      };
      if (main) {
        let node = section;
        while (node && node.parentElement && node.parentElement !== main) {
          node = node.parentElement;
        }
        if (node && node.parentElement === main) {
          autoSwitchHost = node;
          autoSwitchHostIndex = Array.prototype.indexOf.call(main.children, autoSwitchHost);
        }
      }
      const setPinnedContext = (isActive, downReady, upReady) => {
        const pinState = `${isActive ? 1 : 0}-${downReady ? 1 : 0}-${upReady ? 1 : 0}-${autoSwitchHostIndex}`;
        if (pinState !== lastPinnedState) {
          debugLog('setPinnedContext', { isActive, downReady, upReady, autoSwitchHostIndex });
          lastPinnedState = pinState;
        }
        if (!document.body) return;
        if (isActive && autoSwitchHostIndex >= 0) {
          document.body.dataset.mtPinnedSectionIndex = String(autoSwitchHostIndex);
          document.body.dataset.mtPinnedDownReady = downReady ? '1' : '0';
          document.body.dataset.mtPinnedUpReady = upReady ? '1' : '0';
        } else if (document.body.dataset.mtPinnedSectionIndex === String(autoSwitchHostIndex)) {
          delete document.body.dataset.mtPinnedSectionIndex;
          delete document.body.dataset.mtPinnedDownReady;
          delete document.body.dataset.mtPinnedUpReady;
        }
      };
      const setAutoSwitchReady = (downReady, upReady) => {
        const readyState = `${downReady ? 1 : 0}-${upReady ? 1 : 0}`;
        if (readyState !== lastReadyState) {
          debugLog('setAutoSwitchReady', { downReady, upReady, autoSwitchHostIndex });
          lastReadyState = readyState;
        }
        const downValue = downReady ? '1' : '0';
        const upValue = upReady ? '1' : '0';
        section.dataset.mtAutoSwitchDownReady = downValue;
        section.dataset.mtAutoSwitchUpReady = upValue;
        autoSwitchHost.dataset.mtAutoSwitchDownReady = downValue;
        autoSwitchHost.dataset.mtAutoSwitchUpReady = upValue;
      };

      const textScreen1 = section.querySelector('.mt-scroll-text-screen-1');
      const textScreen2 = section.querySelector('.mt-scroll-text-screen-2');
      const textScreen2Content = textScreen2 ? textScreen2.querySelector('.mt-scroll-text-content') : null;
      const screen1Title1 = section.querySelector('.screen-1-title1');
      const screen1Title2 = section.querySelector('.screen-1-title2');
      const screen1Des = section.querySelector('.screen-1-des');
      const screen1Tips = section.querySelector('.screen-1-tips');
      const bgScreen2 = section.querySelector('.mt-scroll-bg-screen-2');
      const blocks = section.querySelectorAll('.mt-scroll-block');
      const blocksContainer = section.querySelector('.mt-scroll-blocks-container');

      if (!textScreen1 || !textScreen2 || !textScreen2Content || !bgScreen2 || blocks.length === 0) {
        console.error('MTScroll: Required elements not found');
        return;
      }

      // 让滚动输入先变“顺滑”，再驱动 ScrollTrigger（对标 test.js 手感）
      setupLenisForScrollTrigger();

      // 计算 section 的总高度（用于 ScrollTrigger）
      // 不再强行设置 section 高度（那会在动效结束后留一大块空白）
      // pin 行程统一由 ScrollTrigger 的 end 控制
      section.style.height = '';

      // 设置初始状态
      gsap.set(textScreen1, { opacity: 1, visibility: 'visible' });
      gsap.set(textScreen2, { opacity: 0, visibility: 'hidden', y: '100%' });
      gsap.set(bgScreen2, { opacity: 0, visibility: 'hidden', y: '100%' });
      const screen1AnimationTargets = [screen1Title1, screen1Title2, screen1Des, screen1Tips].filter(Boolean);
      if (screen1AnimationTargets.length > 0) {
        const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!reducedMotion) {
          gsap.set(screen1AnimationTargets, { opacity: 0, y: 28 });
          ScrollTrigger.create({
            trigger: section,
            start: 'top 80%',
            once: true,
            onEnter: function() {
              gsap.to(screen1AnimationTargets, {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: 'power3.out',
                stagger: 0.12
              });
            }
          });
        } else {
          gsap.set(screen1AnimationTargets, { opacity: 1, y: 0 });
        }
      }
      // 第二屏文字默认可见；后续的淡出/淡入只控制这层
      gsap.set(textScreen2Content, { opacity: 1 });
      // Block初始位置在右侧外
      const blockWidth = 300;
      const windowWidth = window.innerWidth;
      blocks.forEach(block => {
        gsap.set(block, { 
          opacity: 0, 
          visibility: 'hidden', 
          x: windowWidth + blockWidth + 100 
        });
      });

      // 与 scrollerProxy(document.body) 配对；否则 Lenis 滚动与 ScrollTrigger 不同步
      const scrollTriggerVars = {
        trigger: section,
        start: 'top top',
        end: () => `+=${Math.max(1, window.innerHeight) * FEEL.pinVh}`,
        scrub: FEEL.scrub,
        pin: true,
        anticipatePin: 1,
        onUpdate: (self) => {
          // 给 mt-scroll.js 提供“当前 pin 区块是否允许上下切屏”的标记
          const downReady = self.progress >= 0.985;
          const upReady = self.progress <= 0.015;
          setAutoSwitchReady(downReady, upReady);
          setPinnedContext(self.isActive, downReady, upReady);
        },
        onLeave: () => {
          // 离开 pin 后允许继续向下切换
          setAutoSwitchReady(true, false);
          setPinnedContext(false, true, false);
        },
        onLeaveBack: () => {
          // 回到 pin 之前，允许向上切换
          setAutoSwitchReady(false, true);
          setPinnedContext(false, false, true);
        }
      };
      if (window.lenisInstance) {
        scrollTriggerVars.scroller = document.body;
      }

      // 创建ScrollTrigger时间线
      const tl = gsap.timeline({
        scrollTrigger: scrollTriggerVars
      });

      // 尺寸变化时刷新 pin 计算（避免 end 仍按旧 vh 计算导致节奏不对）
      const onResize = () => ScrollTrigger.refresh();
      window.addEventListener('resize', onResize);
      tl.eventCallback('onKill', () => window.removeEventListener('resize', onResize));
      // 第一屏进入后先停留一段滚动距离，再开始切到第二屏
      const firstScreenHoldDuration = 4.0;
      // 第一屏 -> 第二屏切换时长（调大可明显放慢切换体感）
      const screenSwitchDuration = 3.6;

      // 阶段0:
      // 第一屏先停留，不立刻切屏
      tl.to({}, { duration: firstScreenHoldDuration })
      // 阶段1-2:
      // 第一屏保持“固定不动”，仅降低透明度（不隐藏），等待第二屏从下方覆盖上来
      .to(textScreen1, {
        opacity: 0.2,
        duration: screenSwitchDuration,
        ease: 'none'
      })
      // 第二屏背景先覆盖上来（保持不透明），但放在卡片下面
      .to(bgScreen2, {
        opacity: 1,
        visibility: 'visible',
        y: '0%',
        duration: screenSwitchDuration,
        ease: 'none'
      }, '<')
      // 第二屏文字跟着出现（在最上层）
      .to(textScreen2, {
        opacity: 1,
        visibility: 'visible',
        y: '0%',
        duration: screenSwitchDuration,
        ease: 'none'
      }, '<')
      // 进场时保证第二屏文字确实出现（防止上一段把 opacity 留在 0）
      .fromTo(
        textScreen2Content,
        { opacity: 0 },
        { opacity: 1, duration: screenSwitchDuration, ease: 'none' },
        '<'
      )
      // 第二屏已经覆盖上来后，把第一屏彻底隐藏，避免后续任何透明度变化时“透底”看到第一屏
      .set(textScreen1, { visibility: 'hidden' }, '>')
      // 不额外“停留”：否则反向滚动会感觉回第一屏特别慢
      .to({}, { duration: 0.01 }, '>');

      // 阶段3: Blocks 必须在第二屏文字完整出现后才开始
      // 这里改为“切屏完成时间点 + 极小缓冲”，避免视觉上抢跑
      const blocksStartAppear = firstScreenHoldDuration + screenSwitchDuration + 0.15;

      // 阶段4-5: Blocks 连续从右侧出现，保持 10px 间距，
      // 一路向左移动：当自身中心经过「屏幕中间」时刚好回正，然后继续左右 15deg 摇摆着离开（不中途停，也不用等其他 block）
      const defaultBlockGap = -60; // 兼容老数据：block 之间的默认间距
      const moveDurationBase = 45; // 从右到左的总运动时间（越大越慢）
      const blockAppearInterval = 0; // 所有 block 同时出发，依靠 offsetX 保持 10px 间距

      // 阶段3 补充：当大约「第二个 block 已经明显露出来」时，第二屏文字开始淡出
      // 第一个 block 出现时间：blocksStartAppear
      // 第二个 block 出现时间：blocksStartAppear + blockAppearInterval
      // 为了保证一定有 block 在屏幕上，再稍微延后一点
      const fadeOutOffset = window.innerWidth < 767 ? 7 : 7; // PC:+7, M(<767):+10
      const textFadeOutStart = blocksStartAppear + fadeOutOffset;
      const textFadeOutEnd = textFadeOutStart + fadeOutOffset;

      // 注意：不要对 textScreen2 本身做 opacity（否则背景也会变透明 → 透出第一屏）
      // 只淡出第二屏文字内容，背景保持实心，从视觉上仍然是“第二屏覆盖第一屏”
      tl.to(textScreen2Content, {
        opacity: 0,
        duration: textFadeOutEnd - textFadeOutStart,
        ease: 'power2.in'
      }, textFadeOutStart);

      const totalBlocks = blocks.length;

      // 每个 block 的“速度倍率”和“与后一个的间距（px）”，用于计算 offsetX 与每张卡的 duration
      const speedMultipliers = Array.from(blocks).map(block => {
        const el = /** @type {HTMLElement} */ (block);
        const vRaw = el.dataset.speedMultiplier;
        const v = parseFloat(vRaw ?? '');
        return Number.isFinite(v) ? v : 1;
      });
      const gapsToNext = Array.from(blocks).map(block => {
        const el = /** @type {HTMLElement} */ (block);
        const vRaw = el.dataset.gapToNext;
        const v = parseFloat(vRaw ?? '');
        return Number.isFinite(v) ? v : defaultBlockGap;
      });

      // offsetX[i] = sum( blockWidth + gapToNext[k] ) , for k in [0..i-1]
      const offsetsX = new Array(totalBlocks).fill(0);
      for (let i = 1; i < totalBlocks; i++) {
        const gap = gapsToNext[i - 1] ?? defaultBlockGap;
        offsetsX[i] = offsetsX[i - 1] + (blockWidth + gap);
      }
      const lastOffsetX = offsetsX[totalBlocks - 1];

      // 视口中心的 x（考虑容器 padding）：pinned 时容器左缘=0，内容区从 paddingLeft 开始
      const paddingLeft = parseFloat(getComputedStyle(/** @type {HTMLElement} */ (blocksContainer)).paddingLeft) || 0.05 * windowWidth;
      const screenCenterX = windowWidth / 2 - blockWidth / 2 - paddingLeft;

      blocks.forEach((block, index) => {
        const blockIndex = index;
        const isOdd = (blockIndex + 1) % 2 === 1; // 单数
        // 单数 25deg，双数 -25deg
        const initialRotation = isOdd ? -25 : 25;

        // Block 出现的时间点（连续出发）
        const appearTime = blocksStartAppear + (blockIndex * blockAppearInterval);

        // 以 offsetX 控制每张卡的横向间隔（可以逐卡配置），速度由 duration 控制（也可逐卡配置）
        const offsetX = offsetsX[blockIndex];
        const blockDuration = moveDurationBase * (speedMultipliers[blockIndex] || 1);
        // 让所有 block 最终都完全离开屏幕左侧，同时在刚露出时就能看到 25/-25deg：
        // - 起点靠近屏幕右缘（略微在视口外），保证一露头就是大角度倾斜
        // - 终点保证最后一个 block 在时间线结束时也完全出屏幕左侧
        const baseStartX = windowWidth + 20;                       // 第一个 block 的基础起点：紧贴屏幕右缘外侧
        const baseEndX = -blockWidth - 100 - lastOffsetX;          // 第一个 block 的基础终点：更远的左侧

        // 起点 / 终点：在基础路径上整体平移 offsetX，不改变路程长度 → 速度相同，不会出现第二个比第一个“跑得快”
        const startX = baseStartX + offsetX;
        const endX = baseEndX + offsetX;

        // 线性插值：x(progress) = startX + (endX - startX) * progress
        // 求当 x == screenCenterX 时的 progress
        let centerProgress = (screenCenterX - startX) / (endX - startX);
        // 防止数值超界（极端宽屏或配置下）
        centerProgress = Math.max(0, Math.min(1, centerProgress));
        const centerTime = appearTime + blockDuration * centerProgress;

        // 计算「刚从右边出来」的进度：
        // 当 block 的左边缘到达视口右缘附近视为“刚从右边出来”
        const screenEnterX = windowWidth - blockWidth;
        let enterProgress = (screenEnterX - startX) / (endX - startX);
        enterProgress = Math.max(0, Math.min(1, enterProgress));

        // 整体位置运动：从右到左的一条直线运动
        tl.set(block, {
          opacity: 1,
          visibility: 'visible',
          x: startX,
          rotation: initialRotation
        }, appearTime)
        .to(block, {
          x: endX,
          duration: blockDuration,
          ease: 'power2.inOut'
        }, appearTime);

        // rotation 改为在 onUpdate 中根据真实视口位置计算，保证每张卡在同一视觉中心回正
      });

      // 根据每个 block 的真实视口位置动态设置 rotation：
      // - 进入视口右侧时保持 45/-45
      // - 靠近中心时逐渐回正到 0
      // - 经过中心后，一边往左走一边做 ±15deg 的左右摇摆
      const viewportCenterX = () => window.innerWidth / 2;
      const straightenZone = Math.max(500, windowWidth * 0.4); // 进入中心前这段距离内从 45deg 逐渐回正
      const wobbleZone = Math.max(500, windowWidth * 0.4);     // 经过中心后这段距离内做左右摇摆

      tl.eventCallback('onUpdate', function() {
        blocks.forEach((block, index) => {
          if (getComputedStyle(block).visibility === 'hidden') return;
          const rect = block.getBoundingClientRect();
          const blockCenterX = rect.left + rect.width / 2;
          const distFromCenter = blockCenterX - viewportCenterX(); // >0 在中心右侧，<0 在左侧
          const isOdd = (index + 1) % 2 === 1;
          const initialRotation = isOdd ? -15 : 15;

          let rotation;
          if (distFromCenter > straightenZone) {
            // 还在视口右侧很远：保持 45/-45
            rotation = initialRotation;
          } else if (distFromCenter > 0) {
            // 在右侧靠近中心这段距离内：从 45/-45 线性回正到 0
            const t = distFromCenter / straightenZone; // 1 → 0
            rotation = initialRotation * t;
          } else if (distFromCenter > -wobbleZone) {
            // 经过中心后，向左移动这段距离内：做 ±15deg 左右摇摆
            const progress = Math.abs(distFromCenter) / wobbleZone; // 0 → 1
            const wobbleAngle = 15 * Math.sin(progress * Math.PI * 0.8); // 来回摇几次
            rotation = wobbleAngle * (isOdd ? 1 : -1);
          } else {
            // 再往左走远一些：稳定为 0deg
            rotation = 0;
          }
          gsap.set(block, { rotation });
        });
      });

      // 阶段6: 当 block 差不多剩下 2 个时，第二屏文字开始淡入
      const lastBlockIndex = blocks.length - 1;
      const lastBlockAppearTime = blocksStartAppear + (lastBlockIndex * blockAppearInterval);
      const lastBlockTotalDuration = moveDurationBase * (speedMultipliers[lastBlockIndex] || 1);
      const lastBlockEndTime = lastBlockAppearTime + lastBlockTotalDuration;
      const textFadeInTime = lastBlockEndTime - (lastBlockTotalDuration * 0.3); // 消失前段时间开始淡入

      // 卡片快结束时：只让第二屏“文字”淡入（容器一直可见，避免盖住卡片/透底问题）
      // 注意：scrub 下 duration 太短会像“突然出现”，这里显著拉长淡入区间，并强制起点 opacity=0
      tl.set(textScreen2, { visibility: 'visible' }, textFadeInTime)
      .fromTo(
        textScreen2Content,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 1.0,
          ease: 'none'
        },
        textFadeInTime
      );

      // 清理函数
      return function() {
        ScrollTrigger.getAll().forEach(trigger => {
          if (trigger.vars && trigger.vars.trigger === section) {
            trigger.kill();
          }
        });
      };
    }
  };
})();

